import { JSX, useState } from 'react';
import {
    Datagrid,
    DateField,
    FunctionField,
    List,
    SelectInput,
    TextField,
    useListContext,
    useNotify,
    useRefresh,
    useUnselectAll,
    usePermissions,
} from 'react-admin';
import { Button, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { NokProcessingFailure } from '../../types/entities';
import { permissionManager } from '../permissions/manager';
import { rescoreMany, RescoreResult } from '../edit/components/NokProcessing/nokProcessingApi';
import { miniLabel, resultRouteFor, retrySourceLabel, STAGES, stageLabel } from '../edit/components/NokProcessing/failureRoutes';

/**
 * Nok scoring that did not finish.
 *
 * Read-only, deliberately: rows are written by the failure paths in HQ-API and cleared by a
 * rescore. A hand-edited attempt count or status would put the retry logic and the record out of
 * step with no way to tell which was right, so there is no edit or create screen and the API
 * resource exposes no write operations either.
 *
 * The one action is the bulk rescore, which is what this screen exists for: a bug is found and
 * fixed, and everything that failed because of it is sent round again together.
 */

const miniChoices = [
    { id: 'ms', name: 'Mystery Shopper' },
    { id: 'cs', name: 'Compliance Sniffer' },
];

const statusChoices = [
    { id: 'open', name: 'Open' },
    { id: 'retrying', name: 'Retrying' },
    { id: 'resolved', name: 'Resolved' },
];

const filters: JSX.Element[] = [
    <SelectInput key="mini" source="mini" choices={ miniChoices } alwaysOn />,
    <SelectInput key="status" source="status" choices={ statusChoices } alwaysOn />,
    <SelectInput key="stage" source="stage" choices={ STAGES } />,
];

/**
 * Sends the selected rows back to be scored again.
 *
 * Reports HQ-API's own answer rather than a generic success. A rescore is queued for the next
 * end-of-run sweep rather than run on the spot, and a bulk one can queue fewer than it matched
 * when some rows will not reset, which is exactly the thing the person who pressed it needs told.
 */
const RescoreSelectedButton: () => JSX.Element = (): JSX.Element => {
    const { permissions } = usePermissions();

    const { selectedIds } = useListContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const unselectAll = useUnselectAll('nok_processing_failures');
    const [busy, setBusy] = useState<boolean>(false);

    const run = async (): Promise<void> => {
        setBusy(true);
        const result: RescoreResult = await rescoreMany({
            failureIds: selectedIds.map((id: string|number): number => Number(id)),
        });
        notify(result.message, { type: result.status === 'success' ? 'info' : 'warning' });
        setBusy(false);
        if (result.status === 'success') {
            unselectAll();
            refresh();
        }
    };

    // HQ-21. Rescoring costs a full scoring pass per Nok, so it is its own ACL resource and
    // HQ-API refuses it without one. An ungated button is a button that can only fail.
    if (!permissionManager.isAllowed(permissions, 'HQ_Data::nok_rescore')) {
        return <></>;
    }

    return (
        <Button
            size="small"
            disabled={ busy || selectedIds.length === 0 }
            onClick={ (): void => { void run(); } }
        >
            { busy ? 'Queueing' : 'Rescore selected' }
        </Button>
    );
}

export const NokProcessingFailuresList: () => JSX.Element = (): JSX.Element => {
    return (
        <List
            filters={ filters }
            filterDefaultValues={ { status: 'open' } }
            sort={ { field: 'createdAt', order: 'DESC' } }
        >
            <Datagrid bulkActionButtons={ <RescoreSelectedButton /> } rowClick={ false }>
                <TextField source="id" />
                <FunctionField
                    label="Mini"
                    render={ (record: NokProcessingFailure): string => miniLabel(record.mini) }
                />
                { /* The way through to the record this failure is about. Without it the only
                     thing tying a row to the run it broke is an integer to copy out by hand. */ }
                <FunctionField
                    label="Result"
                    render={ (record: NokProcessingFailure): JSX.Element => (
                        <Link
                            component={ RouterLink }
                            to={ resultRouteFor(record.mini, record.resultId) }
                            onClick={ (event): void => { event.stopPropagation(); } }
                        >
                            { record.resultId }
                        </Link>
                    ) }
                />
                <FunctionField
                    label="What happened"
                    render={ (record: NokProcessingFailure): string => stageLabel(record.stage) }
                />
                { /* The reason can be a whole stack trace. The column is the short version and
                     the record's own screen is where the rest belongs. */ }
                <FunctionField
                    label="Reason"
                    render={ (record: NokProcessingFailure): string => {
                        const reason: string = record.reason ?? '';

                        return reason.length > 120 ? reason.slice(0, 120) + '…' : reason;
                    } }
                />
                <TextField source="attempts" label="Tries" />
                <TextField source="autoRetries" label="Auto retries" />
                { /* A customer's rescore spends the same allowance as the automatic one, so the
                     count above is only readable alongside who started the last go. */ }
                <FunctionField
                    label="Last retry"
                    render={ (record: NokProcessingFailure): string => retrySourceLabel(record.lastRetrySource) }
                />
                <TextField source="status" />
                <DateField source="createdAt" label="Failed at" showTime />
            </Datagrid>
        </List>
    );
}
